/**
 * News Articles Loader Web Component
 * Implements Load More functionality for loading additional articles
 * through Shopify Section Rendering API with pagination support
 */

class NewsArticlesLoader extends HTMLElement {
  constructor() {
    super();

    // Initialize variables
    this.isLoading = false;
    this.articlesPerPage = parseInt(this.getAttribute("data-articles-per-page") || "6", 10);
    this.currentPage = parseInt(this.getAttribute("data-current-page") || "1", 10);
    this.totalPages = parseInt(this.getAttribute("data-total-pages") || "1", 10);
    this.totalArticles = parseInt(this.getAttribute("data-total-articles") || "0", 10);

    // Selectors
    this.articlesList = null;
    this.loadMoreButton = null;
    this.loadingIndicator = null;

    // Configuration from data attributes
    this.sectionId = this.getAttribute("data-section-id");
    this.blogHandle = this.getAttribute("data-blog-handle");
    this.buttonText = this.getAttribute("data-button-text") || "Load More";
    this.loadingText = this.getAttribute("data-loading-text") || "Loading...";

    // Derived state
    this.hasMoreArticles = this.currentPage < this.totalPages;

    // Bind methods
    this.handleLoadMore = this.handleLoadMore.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  /**
   * Initialize component
   */
  init() {
    this.articlesList = this.querySelector(".news-articles__list");
    this.loadMoreButton = this.querySelector(".news-articles__button");

    if (!this.articlesList || !this.sectionId) {
      console.error(
        "NewsArticlesLoader: Required elements or attributes not found",
        {
          articlesList: !!this.articlesList,
          loadMoreButton: !!this.loadMoreButton,
          sectionId: !!this.sectionId,
          blogHandle: !!this.blogHandle
        }
      );
      return;
    }

    if (!this.blogHandle) {
      console.error("NewsArticlesLoader: Blog handle is missing!");
      return;
    }

    // If no button found, it means there are no more articles
    if (!this.loadMoreButton) {
      this.hasMoreArticles = false;
      return;
    }

    this.createLoadingIndicator();
    this.attachEventListeners();
  }

  /**
   * Create loading indicator
   */
  createLoadingIndicator() {
    this.loadingIndicator = document.createElement("div");
    this.loadingIndicator.className = "news-articles__loading";
    this.loadingIndicator.style.display = "none";
    this.loadingIndicator.innerHTML = `
      <div class="news-articles__loading-content">
        <div class="news-articles__spinner"></div>
        <span class="news-articles__loading-text">${this.loadingText}</span>
      </div>
    `;

    // Add after button
    this.loadMoreButton.parentNode.insertBefore(
      this.loadingIndicator,
      this.loadMoreButton.nextSibling
    );
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    this.loadMoreButton.addEventListener("click", this.handleLoadMore);
  }

  /**
   * Check initial state - removed as we now rely on Liquid pagination data
   */

  /**
   * Load More button click handler
   */
  async handleLoadMore(event) {
    event.preventDefault();

    if (this.isLoading || !this.hasMoreArticles) {
      return;
    }

    try {
      this.setLoadingState(true);

      const nextPage = this.currentPage + 1;
      const newArticles = await this.fetchArticles(nextPage);

      if (newArticles && newArticles.length > 0) {
        this.appendArticles(newArticles);
        this.currentPage = nextPage;
        this.hasMoreArticles = this.currentPage < this.totalPages;
        if (!this.hasMoreArticles) this.hideLoadMoreButton();
      } else {
        this.hasMoreArticles = false;
        this.hideLoadMoreButton();
      }
    } catch (error) {
      console.error("NewsArticlesLoader: Error loading more articles:", error);
      this.showError();
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Load articles through Section Rendering API
   */
  async fetchArticles(page) {
    const url = this.buildSectionUrl(page);

    if (!url) {
      throw new Error("Failed to build section URL");
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "text/html",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      return this.extractArticlesFromHtml(html);
    } catch (error) {
      console.error("NewsArticlesLoader: Fetch error:", error);
      throw error;
    }
  }

  /**
   * Build URL for Section Rendering API
   */
  buildSectionUrl(page) {
    if (!this.blogHandle) {
      console.error("NewsArticlesLoader: Blog handle is missing");
      return null;
    }

    // Locale-aware base path per Shopify docs
    const root = window.Shopify?.routes?.root || "/";
    const blogPath = `${root.replace(/\/?$/, "/")}blogs/${this.blogHandle}`;

    const url = new URL(blogPath, window.location.origin);
    url.searchParams.set("section_id", this.sectionId);
    url.searchParams.set("page", String(page));

    return url.toString();
  }

  /**
   * Extract articles from HTML response
   */
  extractArticlesFromHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    
    // Try to find articles list in the response
    let articlesList = doc.querySelector(".news-articles__list");
    
    // If not found, the entire response might be the section content
    if (!articlesList) {
      console.warn("NewsArticlesLoader: Articles list not found in response");
      return [];
    }

    const articles = articlesList.querySelectorAll(".news-articles__item");
    return Array.from(articles);
  }

  /**
   * Append new articles to the list
   */
  appendArticles(newArticles) {
    const fragment = document.createDocumentFragment();

    newArticles.forEach((article) => {
      // Clone element to avoid DOM issues
      const clonedArticle = article.cloneNode(true);
      fragment.appendChild(clonedArticle);
    });

    // Add with animation
    this.articlesList.appendChild(fragment);

    // Animate appearance of new articles
    this.animateNewArticles(newArticles.length);
  }

  /**
   * Animate appearance of new articles
   */
  animateNewArticles(count) {
    const allArticles = this.articlesList.querySelectorAll(
      ".news-articles__item"
    );
    const newArticles = Array.from(allArticles).slice(-count);

    newArticles.forEach((article, index) => {
      article.style.opacity = "0";
      article.style.transform = "translateY(20px)";

      setTimeout(() => {
        article.style.transition = "opacity 0.5s ease, transform 0.5s ease";
        article.style.opacity = "1";
        article.style.transform = "translateY(0)";
      }, index * 100);
    });
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    this.isLoading = loading;

    if (loading) {
      this.loadMoreButton.style.display = "none";
      this.loadingIndicator.style.display = "flex";
    } else {
      this.loadingIndicator.style.display = "none";
      if (this.hasMoreArticles) {
        this.loadMoreButton.style.display = "flex";
      }
    }
  }

  /**
   * Hide Load More button
   */
  hideLoadMoreButton() {
    this.loadMoreButton.style.display = "none";
    this.hasMoreArticles = false;
  }

  /**
   * Show error
   */
  showError() {
    const errorMsg = document.createElement("div");
    errorMsg.className = "news-articles__error";
    errorMsg.textContent = "Failed to load more articles. Please try again.";

    this.loadMoreButton.parentNode.insertBefore(errorMsg, this.loadMoreButton);

    // Automatically hide error after 5 seconds
    setTimeout(() => {
      if (errorMsg.parentNode) {
        errorMsg.parentNode.removeChild(errorMsg);
      }
    }, 5000);
  }

  /**
   * Disconnect from DOM
   */
  disconnectedCallback() {
    if (this.loadMoreButton) {
      this.loadMoreButton.removeEventListener("click", this.handleLoadMore);
    }
  }
}

// Register web component
if (typeof customElements !== "undefined") {
  customElements.define("news-articles-loader", NewsArticlesLoader);
}

// Export for possible use in other modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = NewsArticlesLoader;
}
